import { SagaIterator } from 'redux-saga';
import i18next from 'i18next';
import {
  all,
  put,
  call,
  delay,
  takeEvery,
  PutEffect,
} from 'redux-saga/effects';
import get from 'lodash/get';
import { Url, URLS } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import { getDeviationUrl , getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { requestNapi, requestPuppy } from '@wix/da-http-client';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { editDeviationSuccess } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';
import { pushModal, popModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { WriterPopupType } from '../../modals/WriterPopup/redux/types';
import {
  publishWriterDeviation,
  saveWriterDeviation,
  saveWriterDeviationSuccess,
  saveWriterDeviationFailure,
  scheduleDraftPublishSuccess,
} from '../actions';
import {
  convertResponseErrorToWriterError,
  fakeResponse,
  getWriterEndpoint,
} from '../../modals/WriterPopup/redux/utils';
import { PureBiData, SubmitContentBiEvent } from '@wix/da-bi/pkg/events';

const logger = ddt.createLogger('writer.saga');

function* dispatchSaveWriterDeviationFailure(response) {
  yield put(
    saveWriterDeviationFailure(
      convertResponseErrorToWriterError(response?.errorDetails)
    )
  );
}

function* doCreateWriterDeviation(type, groupId, localState) {
  const { journalTitle: title = '', editorState, coverImage } = localState;
  logger.log('doCreateWriterDeviation', { type, groupId, localState });

  const editorRaw = JSON.stringify(editorState?.editorRaw || {});

  return yield call(
    requestNapi,
    {
      method: 'post',
      url: getWriterEndpoint('/create', type),
      data: {
        title,
        group_id: groupId,
        cover_image_deviation_id: coverImage?.deviationId,
        editorRaw,
      },
    },
    fakeResponse
  );
}

function* doUpdateWriterDeviation(type, groupId, deviation, localState) {
  const { journalTitle: title = '', editorState, coverImage } = localState;
  logger.log('doUpdateWriterDeviation', {
    type,
    groupId,
    deviation,
    localState,
  });
  const editorRaw = JSON.stringify(editorState?.editorRaw || {});

  const result = yield call(
    requestNapi,
    {
      method: 'post',
      url: getWriterEndpoint('/update/content', type),
      data: {
        title,
        group_id: groupId,
        deviationid: deviation?.deviationId,
        cover_image_deviation_id: coverImage?.deviationId,
        reset_cover_image_deviation_id: typeof coverImage === 'undefined',
        editorRaw,
      },
    },
    fakeResponse
  );

  if (!result?.deviation) {
    logger.log('failure', result);
    return result;
  }
  if (type === WriterPopupType.LITERATURE) {
    return result;
  }
  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: getWriterEndpoint('/update/metadata', type),
      data: {
        deviationid: deviation?.deviationId,
        group_id: groupId,
        featured: localState.isFeaturedPost,
        tiers:
          localState.visibility?.type === 'tier'
            ? localState.visibility.tiers.map(tier => tier.deviationId)
            : undefined,
      },
    },
    fakeResponse
  );
}

/** Sends a request to our create or update endpoint depending on if the provided deviation exists */
function* doCreateOrUpdateDeviation(type, groupId, deviation, localState) {
  let result: any = null;
  if (deviation?.deviationId === undefined) {
    result = yield call(doCreateWriterDeviation, type, groupId, localState);
  } else {
    result = yield call(
      doUpdateWriterDeviation,
      type,
      groupId,
      deviation,
      localState
    );
  }
  if (result && result?.deviation) {
    yield call(
      handleSuccess,
      { deviation: result?.deviation, type, groupId },
      deviation?.deviationId !== undefined
    );
  } else {
    yield call(dispatchSaveWriterDeviationFailure, result);
  }
  return result;
}

function* handleSuccess({ deviation, type, groupId }, wasEdit = false) {
  const { result: deviationId, entities } = normalize(
    deviation,
    deviationSchema
  );

  const resultActions: PutEffect<any>[] = [
    put(putEntities({ entities })),
    // Even though deviationId is expected to be a number and we're passing through the normalized
    // deviation id (which could be number | string), it should only ever receive
    // a deviation that production an id of type number
    put(saveWriterDeviationSuccess({ deviationId, type })),
  ];

  if (wasEdit) {
    resultActions.push(put(editDeviationSuccess({ deviation })));
  }

  yield all(resultActions);
  logger.log('success', deviationId);
}

function* handleRedirect(url: string) {
  logger.log('redirect', url);
  window.location.assign(url);
}

function* handleSaveWriterDeviation(
  action: ReturnType<typeof saveWriterDeviation>
): SagaIterator {
  const {
    type,
    deviation,
    groupId,
    doSchedule,
    localState,
  } = action.payload;

  const result = yield call(
    doCreateOrUpdateDeviation,
    type,
    groupId,
    deviation,
    localState
  );

  if (result?.deviation) {
    if (doSchedule) {
      yield put(
        pushModal(
          ModalType.DEVIATION_SUBMIT_SCHEDULE,
          {
            params: {
              deviation: result?.deviation,
              scheduleTimestamp:
                result?.deviation?.scheduledPublishTime,
            },
          },
        )
      );
    } else {
      let redirectTo = getDeviationUrl(result.deviation);

      if (type === WriterPopupType.LITERATURE) {
        // Saving a Literature draft should redirect to the sta.sh draft folder
        // but since we do not know the drafts folder hash (yet to be implemented
        // on the backend) we should redirect to the sta.sh root
        redirectTo = Url.buildFromPath(URLS.STASH);

        // If its published we need to redirect back to submit not stash
        if (deviation?.isPublished) {
          redirectTo = Url.submissionLinkWithId(deviation.deviationId);
        }
      }

      yield call(handleRedirect, redirectTo);
    }
  }
}

function* handlePublishWriterDeviation(
  action: ReturnType<typeof publishWriterDeviation>
): SagaIterator {
  const { type, groupId, refreshAfterSubmit, deviation, localState } =
    action.payload;
  logger.log('publishWriterDeviation', action.payload);

  const saveResult = yield call(
    doCreateOrUpdateDeviation,
    type,
    groupId,
    deviation,
    localState
  );

  if (!saveResult || !saveResult.deviation) {
    if (saveResult && saveResult.errorCode === 302) {
      window.location.replace(saveResult.errorDetails.redirectUrl);
    }
    yield call(dispatchSaveWriterDeviationFailure, saveResult);
    return;
  }
  const deviationid = get(saveResult.deviation, 'deviationId', null);

  if (type === WriterPopupType.LITERATURE) {
    // Literature submission should finalize on the submit page
    if (!deviation?.isPublished) {
      yield call(
        logBiEvent,
        PureBiData<SubmitContentBiEvent>({
          evid: 11,
          group_exclusive: groupId !== undefined,
          typeid: ResourceType.DEVIATION,
          itemid: deviationid,
        })
      );
    }
    yield call(handleRedirect, `${Url.submissionLinkWithId(deviationid)}`);
    return;
  }
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: getWriterEndpoint('/publish', type),
      data: {
        deviationid,
        featured: localState.isFeaturedPost,
        group_id: groupId,
        tiers:
          localState.visibility?.type === 'tier'
            ? localState.visibility.tiers.map(tier => tier.deviationId)
            : undefined,
      },
    },
    fakeResponse
  );
  logger.log('response', result);

  if (result && result.deviation) {
    const returnedDeviation = result.deviation;

    yield call(
      logBiEvent,
      PureBiData<SubmitContentBiEvent>({
        evid: 11,
        group_exclusive: groupId !== undefined,
        typeid: ResourceType.JOURNAL,
        itemid: returnedDeviation.deviationId,
      })
    );

    yield call(
      handleSuccess,
      { deviation: returnedDeviation, type, groupId },
      false
    );

    if (groupId) {
      const duration = 5000;
      yield put(
        addNotification(NotificationType.Generic, {
          message: i18next.t('writer.error.pendingApproval'),
          isError: false,
          cardProps: {
            multiLine: true,
            duration,
          },
        })
      );
      yield delay(duration);
    }

    // Upon successful submission, we should redirect
    if (refreshAfterSubmit) {
      window.location.reload();
    } else {
      yield call(handleRedirect, getDeviationUrl(returnedDeviation));
    }
  } else {
    yield call(dispatchSaveWriterDeviationFailure, result);
  }
}

function* handleSaveWriterDeviationFailure(
  action: ReturnType<typeof saveWriterDeviationFailure>
) {
  logger.log('failure', action.payload.error);
}

function* handleScheduleSuccess(
  action: ReturnType<typeof scheduleDraftPublishSuccess>
) {
  const { deviation } = action.payload;
  if (getDeviationType(deviation) === DeviationTypes.JOURNAL) {
    yield put(popModal());
  }
}

export default function* saga(): any {
  yield all([
    takeEvery(saveWriterDeviation, handleSaveWriterDeviation),
    takeEvery(publishWriterDeviation, handlePublishWriterDeviation),
    takeEvery(saveWriterDeviationFailure, handleSaveWriterDeviationFailure),
    takeEvery(scheduleDraftPublishSuccess, handleScheduleSuccess),
  ]);
}
